import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, ReadOnlyNote } from "@/components/Common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Building2, Save, BookOpen, ShieldCheck, UploadCloud, FileDown, Eye, Loader2, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [
  { name: "name", label: "Pharmacy Name" }, { name: "dba", label: "DBA / Trade Name" },
  { name: "address", label: "Address", full: true }, { name: "phone", label: "Phone", mono: true },
  { name: "pic_name", label: "Pharmacist in Charge" }, { name: "npi", label: "NPI", mono: true },
  { name: "dea", label: "DEA Registration", mono: true }, { name: "ncpdp", label: "NCPDP", mono: true },
  { name: "notify_email", label: "Reminder Notification Email" },
];

export default function PharmacyProfile() {
  const { isAdmin } = useAuth();
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [emailChannels, setEmailChannels] = useState([]);

  const load = () => api.get("/pharmacy-profile").then((r) => setForm(r.data || {})).catch(() => {});
  useEffect(() => {
    load();
    api.get("/pharmacy-profile/email-channels")
      .then((r) => setEmailChannels(r.data.channels || []))
      .catch(() => {});
  }, []);

  const save = async () => {
    try { await api.put("/pharmacy-profile", form); toast.success("Profile saved"); }
    catch { toast.error("Save failed"); }
  };

  const uploadInsurance = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = { ...form, insurance_file: data.path, insurance_original_filename: data.original_filename };
      setForm(updated);
      await api.put("/pharmacy-profile", updated);
      toast.success("Insurance plan uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const downloadHandbook = async () => {
    try {
      const res = await api.get("/pharmacy-profile/handbook.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "vera-employee-handbook.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Handbook PDF failed"); }
  };

  return (
    <div>
      <PageHeader testid="profile-header" title="Pharmacy Profile" subtitle="Storefront details, employee handbook, and insurance plan.">
        {!isAdmin && <ReadOnlyNote />}
      </PageHeader>

      <Tabs defaultValue="details" className="w-full max-w-4xl">
        <TabsList className="h-10 bg-slate-100 p-1">
          <TabsTrigger data-testid="pp-tab-details" value="details" className="gap-1.5"><Building2 className="w-4 h-4" /> Details</TabsTrigger>
          <TabsTrigger data-testid="pp-tab-handbook" value="handbook" className="gap-1.5"><BookOpen className="w-4 h-4" /> Employee Handbook</TabsTrigger>
          <TabsTrigger data-testid="pp-tab-insurance" value="insurance" className="gap-1.5"><ShieldCheck className="w-4 h-4" /> Insurance</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-4">
          <Card className="p-6 border-slate-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                  <Label className="text-xs text-slate-600 mb-1 block">{f.label}</Label>
                  <Input data-testid={`profile-field-${f.name}`} value={form[f.name] || ""} disabled={!isAdmin}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className={f.mono ? "font-mono" : ""} />
                </div>
              ))}
            </div>
            {isAdmin && <div className="mt-6 flex justify-end">
              <Button data-testid="profile-save-button" onClick={save} className="bg-pink-700 hover:bg-pink-800 gap-1.5"><Save className="w-4 h-4" /> Save Profile</Button>
            </div>}
          </Card>

          <Card className="p-6 border-slate-200 mt-4" data-testid="notifications-card">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {form.notifications_enabled ? <Bell className="w-5 h-5 text-emerald-700" /> : <BellOff className="w-5 h-5 text-slate-400" />}
                  <h3 className="font-semibold text-slate-900">Outbound Email Notifications</h3>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                    form.notifications_enabled
                      ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                      : "bg-slate-100 text-slate-600 border-slate-300"
                  }`}>{form.notifications_enabled ? "MASTER ON" : "MASTER OFF (default)"}</span>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed max-w-2xl">
                  Master kill-switch + per-channel matrix. When the master is <b>off</b> Vera suppresses every outbound email. When on, only the channels you flip below actually send — everything else stays silent. All switches default to <b>off</b> for HIPAA safety.
                </p>
              </div>
              {isAdmin && (
                <button data-testid="notifications-toggle"
                  onClick={async () => {
                    const next = !form.notifications_enabled;
                    const updated = { ...form, notifications_enabled: next };
                    setForm(updated);
                    try {
                      await api.put("/pharmacy-profile", updated);
                      toast.success(next ? "Master ON — enabled channels will send" : "Master OFF — nothing will send");
                    } catch (e) {
                      setForm({ ...form });
                      toast.error(e.response?.data?.detail || "Toggle failed");
                    }
                  }}
                  role="switch" aria-checked={!!form.notifications_enabled}
                  className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${
                    form.notifications_enabled ? "bg-emerald-600" : "bg-slate-300"
                  }`}>
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    form.notifications_enabled ? "translate-x-6" : "translate-x-1"
                  }`} />
                </button>
              )}
            </div>

            {/* Per-channel matrix */}
            <div className={form.notifications_enabled ? "" : "opacity-50 pointer-events-none"}>
              <div className="text-[10px] uppercase text-slate-500 font-semibold mb-2 flex items-center justify-between">
                <span>Channels ({emailChannels.filter((c) => (form.notifications?.channels || {})[c.key]).length} / {emailChannels.length} enabled)</span>
                {!form.notifications_enabled && <span className="normal-case text-slate-400 italic">Turn master ON to edit</span>}
              </div>
              <div className="space-y-1.5">
                {emailChannels.map((c) => {
                  const on = !!(form.notifications?.channels || {})[c.key];
                  return (
                    <div key={c.key} data-testid={`channel-row-${c.key}`}
                      className="flex items-start justify-between gap-3 p-3 rounded-md border border-slate-200 bg-slate-50/40">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-800">{c.label}</span>
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${
                            on ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                               : "bg-slate-100 text-slate-500 border-slate-300"
                          }`}>{on ? "ON" : "OFF"}</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">{c.hint}</p>
                      </div>
                      {isAdmin && (
                        <button data-testid={`channel-toggle-${c.key}`}
                          disabled={!form.notifications_enabled}
                          onClick={async () => {
                            const currentChannels = form.notifications?.channels || {};
                            const nextChannels = { ...currentChannels, [c.key]: !on };
                            const updated = { ...form, notifications: { channels: nextChannels } };
                            setForm(updated);
                            try {
                              await api.put("/pharmacy-profile", updated);
                              toast.success(`${c.label} — ${!on ? "ON" : "OFF"}`);
                            } catch (e) {
                              setForm({ ...form });
                              toast.error(e.response?.data?.detail || "Toggle failed");
                            }
                          }}
                          role="switch" aria-checked={on}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                            on ? "bg-emerald-600" : "bg-slate-300"
                          } disabled:opacity-60`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            on ? "translate-x-6" : "translate-x-1"
                          }`} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="handbook" className="mt-4">
          <Card className="p-6 border-slate-200">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-sm text-slate-500">Edit once, publish everywhere. Employees download the PDF from /clockin.</p>
              <div className="flex gap-2">
                <input type="file" accept="application/pdf,image/*" className="hidden" data-testid="handbook-ai-file"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const fd = new FormData(); fd.append("file", f);
                    toast.info("AI reformatting handbook — may take up to a minute…");
                    try {
                      const { data } = await api.post("/handbook/ai-convert", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setForm((prev) => ({ ...prev, handbook_content: data.handbook_content }));
                      toast.success("Handbook reformatted — review and save");
                    } catch (err) { toast.error(err.response?.data?.detail || "AI conversion failed"); }
                    finally { e.target.value = ""; }
                  }} />
                {isAdmin && (
                  <Button data-testid="handbook-ai-btn" variant="outline"
                    onClick={(e) => e.currentTarget.previousSibling.click()}
                    className="border-pink-300 text-pink-800 hover:bg-pink-50 gap-1.5">
                    <BookOpen className="w-4 h-4" /> AI Convert
                  </Button>
                )}
                <Button data-testid="download-handbook-btn" variant="outline" onClick={downloadHandbook} className="gap-1.5"><FileDown className="w-4 h-4" /> Download PDF</Button>
              </div>
            </div>
            <Textarea data-testid="handbook-content" rows={22} value={form.handbook_content || ""} disabled={!isAdmin}
              onChange={(e) => setForm({ ...form, handbook_content: e.target.value })}
              placeholder="Write the handbook here. Blank lines create paragraph breaks in the PDF.&#10;&#10;Example:&#10;1. Welcome to Vera Pharmacy&#10;   Our mission and values...&#10;&#10;2. Attendance & Time Clock&#10;   All employees clock in at /clockin using their 4-digit PIN..." />
            {isAdmin && <div className="mt-4 flex justify-end">
              <Button data-testid="handbook-save" onClick={save} className="bg-pink-700 hover:bg-pink-800 gap-1.5"><Save className="w-4 h-4" /> Save & Publish</Button>
            </div>}
          </Card>
        </TabsContent>

        <TabsContent value="insurance" className="mt-4">
          <Card className="p-6 border-slate-200">
            <p className="text-sm text-slate-500 mb-4">Upload the company's health / dental / vision plan PDF. Every employee can download it from /clockin.</p>
            {form.insurance_file ? (
              <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg mb-4">
                <div>
                  <div className="font-medium text-slate-800">{form.insurance_original_filename || "insurance-plan.pdf"}</div>
                  <div className="text-xs text-slate-400 font-mono">on file</div>
                </div>
                <Button data-testid="view-insurance" variant="outline" size="sm" className="gap-1.5" onClick={() => window.open(fileUrl(form.insurance_file), "_blank")}><Eye className="w-4 h-4" /> View</Button>
              </div>
            ) : <p className="text-sm text-slate-400 mb-4">No plan uploaded yet.</p>}
            {isAdmin && (
              <label className="inline-flex">
                <input type="file" accept="application/pdf,image/*" className="hidden" onChange={uploadInsurance} data-testid="insurance-upload" />
                <Button as="span" disabled={uploading} className="bg-pink-700 hover:bg-pink-800 gap-1.5 cursor-pointer" onClick={(e) => e.currentTarget.previousSibling.click()}>
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} {form.insurance_file ? "Replace" : "Upload"} Insurance Plan
                </Button>
              </label>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
